import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationCircle,
  FaFileAlt,
  FaPlus,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { db } from "../firebase";
import "./legalAgreement.css";

const DEFAULT_CATEGORIES = [
  "USER TERMS AND CONDITIONS",
  "USER PRIVACY AND POLICY",
  "USER LEGAL",
  "ARTIST TERMS AND CONDITIONS",
  "ARTIST PRIVACY AND POLICY",
  "ARTIST LEGAL",
  "ARTIST AGREEMENT",
  "MENTOR AGREEMENT",
  "EVENT ORGANISER AGREEMENT",
];

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  danger: "#b91c1c",
};

export default function LegalAgreement() {
  const [activeTab, setActiveTab] = useState("create");
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORIES[0]);
  const [newCategory, setNewCategory] = useState("");

  const [effectiveDate, setEffectiveDate] = useState(dateInputValue(new Date()));
  const [lastUpdatedInput, setLastUpdatedInput] = useState(dateTimeInputValue(new Date()));
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const [activeDoc, setActiveDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ effectiveDate: "", content: "" });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [popup, setPopup] = useState(null);
  const popupTimerRef = useRef(null);

  const docsByCategory = useMemo(() => {
    const map = new Map();
    allDocs.forEach((item) => map.set(toCategoryKey(item.category), item));
    return map;
  }, [allDocs]);

  const selectedCategoryKey = useMemo(
    () => toCategoryKey(selectedCategory),
    [selectedCategory]
  );

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "all_legal_files"),
      (snap) => {
        const docs = snap.docs
          .map((item) => {
            const data = item.data() || {};
            const category = cleanCategoryName(data.category || data.title || item.id);
            return {
              id: item.id,
              category,
              title: data.title || category,
              effectiveDate: String(data.effectiveDate || ""),
              lastUpdated: data.lastUpdated || "",
              createdAt: data.createdAt || null,
              updatedAt: data.updatedAt || null,
              content: String(data.content || ""),
            };
          })
          .sort((a, b) => toMillis(b) - toMillis(a));

        setAllDocs(docs);
        setCategories((prev) => uniqueItems([...DEFAULT_CATEGORIES, ...prev, ...docs.map((d) => d.category)]));
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load legal files:", error);
        showPopup("Failed to load legal documents.", "error", setPopup, popupTimerRef);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const timerRef = popupTimerRef;
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const existing = docsByCategory.get(selectedCategoryKey);
    if (existing) {
      setEffectiveDate(existing.effectiveDate || dateInputValue(new Date()));
      setLastUpdatedInput(dateTimeInputValue(asDate(existing.lastUpdated || existing.updatedAt || new Date())));
      setContent(existing.content || "");
      return;
    }

    setEffectiveDate(dateInputValue(new Date()));
    setLastUpdatedInput(dateTimeInputValue(new Date()));
    setContent("");
  }, [docsByCategory, selectedCategoryKey]);

  useEffect(() => {
    if (!activeDoc) return;
    const latest = allDocs.find((item) => item.id === activeDoc.id);
    if (!latest) {
      setActiveDoc(null);
      setIsEditing(false);
      return;
    }
    setActiveDoc(latest);
  }, [allDocs, activeDoc]);

  const currentTitle = selectedCategory || "Select Category";
  const selectedExistingDoc = docsByCategory.get(selectedCategoryKey) || null;

  const handleAddCategory = () => {
    const next = cleanCategoryName(newCategory);
    if (!next) {
      showPopup("Enter a category name first.", "error", setPopup, popupTimerRef);
      return;
    }
    setCategories((prev) => uniqueItems([...prev, next]));
    setSelectedCategory(next);
    setNewCategory("");
    showPopup("Category added in dropdown.", "success", setPopup, popupTimerRef);
  };

  const handleSaveLegal = async () => {
    if (!selectedCategory) {
      showPopup("Please choose one category.", "error", setPopup, popupTimerRef);
      return;
    }
    if (!content.trim()) {
      showPopup("Please add legal text before updating.", "error", setPopup, popupTimerRef);
      return;
    }

    setSaving(true);
    try {
      const categoryName = cleanCategoryName(selectedCategory);
      const key = toCategoryKey(categoryName);
      const docRef = doc(db, "all_legal_files", key);
      const exists = Boolean(selectedExistingDoc);

      const payload = {
        category: categoryName,
        title: categoryName,
        effectiveDate: effectiveDate || dateInputValue(new Date()),
        lastUpdated: lastUpdatedInput || dateTimeInputValue(new Date()),
        content,
        updatedAt: serverTimestamp(),
      };

      if (!exists) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(docRef, payload, { merge: true });
      showPopup(
        exists ? "Legal document updated successfully." : "Legal document created successfully.",
        "success",
        setPopup,
        popupTimerRef
      );
    } catch (error) {
      console.error("Failed to save legal document:", error);
      showPopup("Failed to save legal document.", "error", setPopup, popupTimerRef);
    } finally {
      setSaving(false);
    }
  };

  const openDoc = (item) => {
    setActiveDoc(item);
    setIsEditing(false);
    setEditForm({
      effectiveDate: item.effectiveDate || dateInputValue(new Date()),
      content: item.content || "",
    });
  };

  const startEdit = () => {
    if (!activeDoc) return;
    setEditForm({
      effectiveDate: activeDoc.effectiveDate || dateInputValue(new Date()),
      content: activeDoc.content || "",
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!activeDoc) return;
    if (!editForm.content.trim()) {
      showPopup("Legal text cannot be empty.", "error", setPopup, popupTimerRef);
      return;
    }

    setUpdating(true);
    try {
      const autoNow = new Date();

      await updateDoc(doc(db, "all_legal_files", activeDoc.id), {
        effectiveDate: editForm.effectiveDate || dateInputValue(new Date()),
        content: editForm.content,
        lastUpdated: autoNow.toISOString(),
        updatedAt: serverTimestamp(),
      });

      setIsEditing(false);
      showPopup("Legal document edited successfully.", "success", setPopup, popupTimerRef);
    } catch (error) {
      console.error("Failed to edit legal document:", error);
      showPopup("Failed to edit legal document.", "error", setPopup, popupTimerRef);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!activeDoc) return;
    const yes = window.confirm(`Delete "${activeDoc.category}" legal document?`);
    if (!yes) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "all_legal_files", activeDoc.id));
      setActiveDoc(null);
      setIsEditing(false);
      showPopup("Legal document deleted successfully.", "success", setPopup, popupTimerRef);
    } catch (error) {
      console.error("Failed to delete legal document:", error);
      showPopup("Failed to delete legal document.", "error", setPopup, popupTimerRef);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.topCard}>
        <h2 style={styles.title}>Leagl&AGREMENT</h2>
        <p style={styles.subtitle}>
          Create and manage legal documents in Firestore collection
          <strong> all_legal_files</strong>.
        </p>

        <div style={styles.tabRow}>
          <button
            type="button"
            style={{ ...styles.tabButton, ...(activeTab === "create" ? styles.tabButtonActive : {}) }}
            onClick={() => setActiveTab("create")}
          >
            Create Legal
          </button>
          <button
            type="button"
            style={{ ...styles.tabButton, ...(activeTab === "manage" ? styles.tabButtonActive : {}) }}
            onClick={() => setActiveTab("manage")}
          >
            Manage Legal
          </button>
        </div>
      </div>

      {activeTab === "create" ? (
        <section style={styles.card}>
          <label style={styles.label}>Category (one at a time)</label>
          <div style={styles.rowWrap}>
            <select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              style={styles.input}
            >
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.rowWrap}>
            <input
              value={newCategory}
              onChange={(event) => setNewCategory(event.target.value)}
              style={styles.input}
              placeholder="Add more category if required"
            />
            <button type="button" style={styles.secondaryBtn} onClick={handleAddCategory}>
              <FaPlus />
              Add Category
            </button>
          </div>

          <div style={styles.docTitle}>{currentTitle}</div>

          <div style={styles.twoCol}>
            <label style={styles.labelBlock}>
              <span>Effective Date: [Insert Date]</span>
              <input
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                style={styles.input}
              />
            </label>
            <label style={styles.labelBlock}>
              <span>Last Updated: [Insert Date]</span>
              <input
                type="datetime-local"
                value={lastUpdatedInput}
                onChange={(event) => setLastUpdatedInput(event.target.value)}
                style={styles.input}
              />
            </label>
          </div>

          <label style={styles.label}>Legal Text (no limit)</label>
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            style={styles.textarea}
            placeholder="Paste legal text here..."
          />

          <div style={styles.footerRow}>
            <button type="button" style={styles.primaryBtn} disabled={saving} onClick={handleSaveLegal}>
              {saving ? "Saving..." : "Update Now"}
            </button>
            {selectedExistingDoc ? (
              <span style={styles.helperText}>This category already exists. Update Now will edit it.</span>
            ) : null}
          </div>
        </section>
      ) : (
        <section style={styles.card}>
          <div style={styles.sectionHead}>
            <h3 style={{ margin: 0 }}>Manage Legal Documents</h3>
            <span style={styles.mutedCount}>{allDocs.length} items</span>
          </div>

          {loading ? (
            <div style={styles.emptyBox}>Loading legal documents...</div>
          ) : allDocs.length === 0 ? (
            <div style={styles.emptyBox}>No legal document found yet.</div>
          ) : (
            <div className="legal-cards-grid">
              {allDocs.map((item) => (
                <button key={item.id} type="button" style={styles.docCard} onClick={() => openDoc(item)}>
                  <div style={styles.docCardTop}>
                    <FaFileAlt color={COLORS.primary} />
                    <span style={styles.docCardTitle}>{item.category}</span>
                  </div>
                  <div style={styles.docMeta}>
                    <strong>Effective:</strong> {formatDate(item.effectiveDate)}
                  </div>
                  <div style={styles.docMeta}>
                    <strong>Last Updated:</strong> {formatDateTime(item.lastUpdated || item.updatedAt)}
                  </div>
                  <div style={styles.preview}>{truncate(item.content, 150)}</div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {activeDoc ? (
        <div style={styles.modalBackdrop} onClick={() => !updating && !deleting && setActiveDoc(null)}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ margin: 0 }}>{activeDoc.category}</h3>
                <p style={styles.modalMeta}>
                  Effective Date: {formatDate(activeDoc.effectiveDate)} | Last Updated:{" "}
                  {formatDateTime(activeDoc.lastUpdated || activeDoc.updatedAt)}
                </p>
              </div>
              <button
                type="button"
                style={styles.iconBtn}
                onClick={() => setActiveDoc(null)}
                aria-label="Close"
                disabled={updating || deleting}
              >
                <FaTimes />
              </button>
            </div>

            {isEditing ? (
              <>
                <label style={styles.labelBlock}>
                  <span>Effective Date</span>
                  <input
                    type="date"
                    value={editForm.effectiveDate}
                    onChange={(event) =>
                      setEditForm((prev) => ({ ...prev, effectiveDate: event.target.value }))
                    }
                    style={styles.input}
                  />
                </label>
                <label style={styles.labelBlock}>
                  <span>Legal Text</span>
                  <textarea
                    value={editForm.content}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, content: event.target.value }))}
                    style={{ ...styles.textarea, minHeight: 320 }}
                  />
                </label>
                <p style={styles.infoLine}>On save, Last Updated date/time is auto-updated.</p>
                <div style={styles.modalActions}>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => setIsEditing(false)}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={styles.primaryBtn}
                    onClick={handleSaveEdit}
                    disabled={updating}
                  >
                    {updating ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.modalViewer}>{activeDoc.content || "No legal text available."}</div>
                <div style={styles.modalActions}>
                  <button type="button" style={styles.secondaryBtn} onClick={startEdit}>
                    <FaEdit />
                    Edit
                  </button>
                  <button
                    type="button"
                    style={styles.deleteBtn}
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    <FaTrash />
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {popup ? (
        <div
          style={{
            ...styles.popup,
            ...(popup.type === "error" ? styles.popupError : styles.popupSuccess),
          }}
        >
          {popup.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span>{popup.message}</span>
        </div>
      ) : null}
    </div>
  );
}

function showPopup(message, type, setPopup, timerRef) {
  setPopup({ message, type });
  if (timerRef.current) clearTimeout(timerRef.current);
  timerRef.current = setTimeout(() => setPopup(null), 2500);
}

function uniqueItems(list) {
  const normalized = [];
  list.forEach((raw) => {
    const value = cleanCategoryName(raw);
    if (!value) return;
    if (!normalized.includes(value)) normalized.push(value);
  });
  return normalized;
}

function cleanCategoryName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function toCategoryKey(label) {
  return cleanCategoryName(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function asDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function toMillis(item) {
  const updated = asDate(item.updatedAt || item.lastUpdated);
  if (updated) return updated.getTime();
  const created = asDate(item.createdAt);
  if (created) return created.getTime();
  return 0;
}

function dateInputValue(date) {
  const base = asDate(date) || new Date();
  const year = base.getFullYear();
  const month = String(base.getMonth() + 1).padStart(2, "0");
  const day = String(base.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateTimeInputValue(date) {
  const base = asDate(date) || new Date();
  const year = base.getFullYear();
  const month = String(base.getMonth() + 1).padStart(2, "0");
  const day = String(base.getDate()).padStart(2, "0");
  const hour = String(base.getHours()).padStart(2, "0");
  const minute = String(base.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function formatDate(value) {
  const date = asDate(value);
  if (!date) return "Not set";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  const date = asDate(value);
  if (!date) return "Not set";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncate(value, length = 120) {
  const text = String(value || "");
  if (text.length <= length) return text || "No preview available.";
  return `${text.slice(0, length)}...`;
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: 16,
    display: "grid",
    gap: 12,
    background: COLORS.page,
    fontFamily: "Inter, system-ui, sans-serif",
  },
  topCard: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    borderRadius: 14,
    color: "#fff",
    padding: "16px 18px",
    boxShadow: "0 14px 30px rgba(0,0,0,0.12)",
  },
  title: { margin: 0, fontSize: 24, fontWeight: 900 },
  subtitle: { margin: "6px 0 0", opacity: 0.95, fontSize: 13 },
  tabRow: { marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" },
  tabButton: {
    border: "1px solid rgba(255,255,255,0.45)",
    background: "rgba(255,255,255,0.18)",
    color: "#fff",
    borderRadius: 10,
    padding: "9px 16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  tabButtonActive: {
    background: "#fff",
    color: COLORS.primary,
  },
  card: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    display: "grid",
    gap: 10,
  },
  label: {
    color: COLORS.subtext,
    fontWeight: 700,
    fontSize: 13,
  },
  labelBlock: {
    display: "grid",
    gap: 6,
    color: COLORS.subtext,
    fontWeight: 700,
    fontSize: 13,
  },
  rowWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
  },
  input: {
    width: "100%",
    minWidth: 180,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    height: 40,
    padding: "0 12px",
    fontSize: 14,
    color: COLORS.text,
    background: "#fff",
  },
  textarea: {
    width: "100%",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    minHeight: 280,
    resize: "vertical",
    padding: 12,
    fontSize: 14,
    lineHeight: 1.55,
    color: COLORS.text,
  },
  docTitle: {
    fontWeight: 900,
    fontSize: 16,
    color: COLORS.text,
    padding: "8px 10px",
    borderRadius: 10,
    background: "#f8fafc",
    border: `1px solid ${COLORS.border}`,
  },
  footerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  helperText: {
    color: COLORS.subtext,
    fontSize: 12,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 10,
    height: 40,
    padding: "0 16px",
    fontWeight: 800,
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  secondaryBtn: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    height: 40,
    padding: "0 14px",
    fontWeight: 800,
    background: "#fff",
    color: COLORS.text,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  deleteBtn: {
    border: "1px solid #fecaca",
    borderRadius: 10,
    height: 40,
    padding: "0 14px",
    fontWeight: 800,
    background: "#fff",
    color: COLORS.danger,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 6,
  },
  mutedCount: {
    color: COLORS.subtext,
    fontWeight: 700,
    fontSize: 12,
  },
  emptyBox: {
    border: `1px dashed ${COLORS.border}`,
    borderRadius: 12,
    padding: 18,
    textAlign: "center",
    color: COLORS.subtext,
    fontWeight: 700,
    background: "#f8fafc",
  },
  docCard: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    background: "#fff",
    padding: 12,
    textAlign: "left",
    display: "grid",
    gap: 7,
    cursor: "pointer",
    color: COLORS.text,
    boxShadow: "0 5px 14px rgba(2,6,23,0.05)",
  },
  docCardTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900,
    minHeight: 42,
  },
  docCardTitle: {
    fontSize: 13,
    lineHeight: 1.4,
  },
  docMeta: {
    fontSize: 12,
    color: COLORS.subtext,
  },
  preview: {
    fontSize: 12,
    lineHeight: 1.5,
    color: COLORS.text,
    background: "#f8fafc",
    borderRadius: 8,
    padding: 8,
    minHeight: 72,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 80,
  },
  modalCard: {
    width: "100%",
    maxWidth: 900,
    maxHeight: "92vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    padding: 14,
    display: "grid",
    gap: 10,
    boxShadow: "0 18px 44px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  iconBtn: {
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    width: 34,
    height: 34,
    borderRadius: 8,
    color: COLORS.text,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  modalMeta: {
    margin: "4px 0 0",
    fontSize: 12,
    color: COLORS.subtext,
  },
  modalViewer: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    background: "#f8fafc",
    padding: 12,
    minHeight: 320,
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    color: COLORS.text,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  infoLine: {
    margin: 0,
    color: COLORS.subtext,
    fontSize: 12,
    fontWeight: 700,
  },
  popup: {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 120,
    borderRadius: 10,
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 800,
    boxShadow: "0 10px 24px rgba(0,0,0,0.16)",
  },
  popupSuccess: {
    background: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  popupError: {
    background: "#fff1f2",
    border: "1px solid #fda4af",
    color: "#9f1239",
  },
};
